import { createFileRoute, Link, Outlet, redirect, useRouter } from "@tanstack/react-router";
import {
  Award, BadgeCheck, Bell, BookMarked, BookOpen, Briefcase, Calendar, FileText, FlaskConical,
  GraduationCap, HelpCircle, Image as ImageIcon, LayoutDashboard, LogOut, Library, MessageSquare,
  MessageSquareQuote, Newspaper, PanelLeft, Layers, Receipt, ScrollText, Settings, ShieldAlert, ShieldCheck, Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_app")({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.user) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: AppShell,
});

type NavItem = { to: string; label: string; icon: React.ComponentType<{ className?: string }>; perm?: [string, string] };
type NavGroup = { heading?: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
    ],
  },
  {
    heading: "Content",
    items: [
      { to: "/content/pages",         label: "Pages",         icon: FileText,  perm: ["pages", "read"] },
      { to: "/content/news",          label: "News",          icon: Newspaper, perm: ["news", "read"] },
      { to: "/content/blog",          label: "Blog",          icon: Newspaper, perm: ["blog", "read"] },
      { to: "/content/announcements", label: "Announcements", icon: Bell,      perm: ["announcements", "read"] },
      { to: "/popups",                label: "Popups",        icon: Bell,      perm: ["popups", "read"] },
      { to: "/media",                 label: "Media",         icon: ImageIcon, perm: ["media", "read"] },
    ],
  },
  {
    heading: "Departments",
    items: [
      { to: "/departments/overviews",             label: "Overviews",             icon: LayoutDashboard,    perm: ["department_overviews", "read"] },
      { to: "/departments/facilities",            label: "Facilities",            icon: LayoutDashboard,    perm: ["facilities", "read"] },
      { to: "/departments/pedagogy",              label: "Pedagogy",              icon: LayoutDashboard,    perm: ["pedagogy", "read"] },
      { to: "/departments/curriculum-highlights", label: "Curriculum Highlights", icon: LayoutDashboard,    perm: ["curriculum_highlights", "read"] },
      { to: "/departments/graduation",            label: "Graduation",            icon: GraduationCap,      perm: ["graduation", "read"] },
      { to: "/departments/faculty",               label: "Faculty",               icon: GraduationCap,      perm: ["faculty", "read"] },
      { to: "/departments/regulations",           label: "Regulations",           icon: BookMarked,         perm: ["regulations", "read"] },
      { to: "/departments/curriculum",            label: "Curriculum",            icon: BookOpen,           perm: ["curriculum", "read"] },
      { to: "/departments/testimonials",          label: "Testimonials",          icon: MessageSquareQuote, perm: ["testimonials", "read"] },
      { to: "/departments/achievements",          label: "Achievements",          icon: Award,              perm: ["achievements", "read"] },
      { to: "/departments/placements",            label: "Placements",            icon: Briefcase,          perm: ["placements", "read"] },
      { to: "/departments/nptel",                 label: "Online Certifications", icon: BadgeCheck,         perm: ["nptel", "read"] },
      { to: "/departments/internships",           label: "Internships",           icon: Briefcase,          perm: ["internships", "read"] },
      { to: "/departments/research",              label: "Research",              icon: FlaskConical,       perm: ["research", "read"] },
      { to: "/departments/menus",                 label: "Menu Items",            icon: PanelLeft,          perm: ["dept_tabs", "read"] },
      { to: "/departments/tabs",                  label: "Sub-tabs",              icon: Layers,             perm: ["dept_tabs", "read"] },
      { to: "/departments/e-contents",            label: "E-Contents",            icon: Library,            perm: ["e_contents", "read"] },
      { to: "/departments/publications",          label: "Newsletter & Magazine", icon: Newspaper,          perm: ["dept_publications", "read"] },
      { to: "/departments/faqs",                  label: "FAQs",                  icon: HelpCircle,         perm: ["faqs", "read"] },
    ],
  },
  {
    heading: "Examinations",
    items: [
      { to: "/coe/documents", label: "COE Documents", icon: FileText, perm: ["coe_documents", "read"] },
      { to: "/coe/updates",   label: "COE Updates",   icon: Bell,     perm: ["coe_updates", "read"] },
    ],
  },
  {
    // All four share the single `cfrd` resource — see krct-api/src/lib/jwt.ts
    // for why the permission catalogue is not split per table.
    heading: "Research (CFRD)",
    items: [
      { to: "/cfrd/supervisors", label: "Supervisors", icon: GraduationCap, perm: ["cfrd", "read"] },
      { to: "/cfrd/facilities",  label: "Laboratories", icon: FlaskConical, perm: ["cfrd", "read"] },
      { to: "/cfrd/activities",  label: "Activities",   icon: Calendar,     perm: ["cfrd", "read"] },
      { to: "/cfrd/gallery",     label: "Gallery",      icon: ImageIcon,    perm: ["cfrd", "read"] },
    ],
  },
  {
    heading: "Events & Payments",
    items: [
      { to: "/events",   label: "Events",   icon: Calendar, perm: ["events", "read"] },
      { to: "/payments", label: "Payments", icon: Receipt,  perm: ["payments", "read"] },
    ],
  },
  {
    heading: "Chatbot",
    items: [
      { to: "/chatbot/conversations",  label: "Conversations",  icon: MessageSquare, perm: ["chatbot", "read_conversations"] },
      { to: "/chatbot/flagged",        label: "Flagged",        icon: ShieldAlert,   perm: ["chatbot", "read_conversations"] },
      { to: "/chatbot/knowledge-base", label: "Knowledge Base", icon: BookOpen,      perm: ["knowledge_base", "create"] },
    ],
  },
  {
    // Confidential. The `icc_complaints` permission is granted to the
    // `icc_member` role only, so this group is invisible to editors, viewers,
    // department heads and events officers — see krct-api/src/db/seed.sql.
    heading: "Internal Committee",
    items: [
      { to: "/icc-complaints", label: "Complaints", icon: ScrollText, perm: ["icc_complaints", "read"] },
    ],
  },
  {
    heading: "Administration",
    items: [
      { to: "/users",     label: "Users",     icon: Users,       perm: ["users", "read"] },
      { to: "/roles",     label: "Roles",     icon: ShieldCheck, perm: ["roles", "read"] },
      { to: "/audit-log", label: "Audit Log", icon: ShieldCheck, perm: ["audit_log", "read"] },
      { to: "/settings",  label: "Settings",  icon: Settings,    perm: ["settings", "read"] },
    ],
  },
];

function AppShell() {
  const { user, signOut, can } = useAuth();
  const router = useRouter();

  const visibleGroups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((n) => !n.perm || can(n.perm[0], n.perm[1])) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="grid h-screen grid-cols-[16rem_1fr] grid-rows-[3.5rem_1fr]">
      {/* Topbar */}
      <header className="col-span-2 flex items-center justify-between border-b bg-background px-4">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold text-xs">KR</div>
          <span className="font-semibold">KRCT Admin</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">{user?.name}</span>
          <Button variant="ghost" size="sm" onClick={async () => { await signOut(); router.navigate({ to: "/login" }); }}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="row-start-2 overflow-y-auto border-r bg-card p-2">
        <nav className="flex flex-col gap-0.5">
          {visibleGroups.map((group, gi) => (
            <div key={group.heading ?? `group-${gi}`} className={gi > 0 ? "mt-3" : undefined}>
              {group.heading && (
                <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {group.heading}
                </p>
              )}
              {group.items.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  activeOptions={{ exact: n.to === "/" }}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground [&.active]:bg-secondary [&.active]:font-medium [&.active]:text-foreground"
                  activeProps={{ className: "active" }}
                >
                  <n.icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{n.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <main className="row-start-2 overflow-y-auto bg-secondary/40 p-6">
        <Outlet />
      </main>
    </div>
  );
}
